import * as React from 'react';
import clsx from 'clsx';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { usePersonalityConfig } from '../../hooks/usePersonalityConfig';
import { Button } from '@/components/ui/Button';
import { ConfigSlider } from './ConfigSlider';
import { ConfigTags } from './ConfigTags';
import { BannedContentNotice } from './BannedContentNotice';
import { ConfigListInput } from './ConfigListInput';

import {
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  BoltIcon,
  BuildingOfficeIcon,
  TrophyIcon,
  UserGroupIcon,
  HeartIcon,
  LightBulbIcon,
  PencilSquareIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline';

import { CONFIG_TOPICS, CONFIG_APPROACH, CONFIG_INTERACTION, CONFIG_HOBBIES } from '../../data';

import type { PersonalityConfigSectionProps } from '../../types';

/** UI for fine-tuning Echo personality: sliders, tags, core values, and content policy. */
export const PersonalityConfigSection: React.FC<PersonalityConfigSectionProps> = ({
  draftId,
  onBack,
  onComplete,
  onSaveForLater,
  initialConfig,
}) => {
  const {
    config,
    updateField,
    resetField,
    resetAll,
    isFieldModified,
    hasModifications,
    handleSubmit,
    saveDraft,
    isSubmitting,
    isLoading,
  } = usePersonalityConfig(draftId, onComplete, initialConfig);

  const handleSaveClick = async () => {
    const success = await saveDraft();
    if (success) {
      onSaveForLater();
    }
  };

  const contentClass =
    isSubmitting || isLoading ? 'opacity-50 pointer-events-none transition-opacity' : '';

  if (isLoading) {
    return (
      <div className='animate-slide-in w-full wide:mx-auto wide:max-w-[1200px]'>
        <div className='flex items-center justify-center py-12'>
          <div className='text-center'>
            <div className='mb-4 inline-block size-12 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]' />
            <p className='text-lg text-gray-300'>Loading your personality preview from survey...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='animate-slide-in w-full'>
      <div className='mb-2 flex items-center gap-3'>
        <button
          type='button'
          onClick={onBack}
          disabled={isSubmitting}
          className='rounded-full p-1 transition-colors hover:bg-gray-100 disabled:opacity-50'
          aria-label='Go back'
        >
          <ArrowLeftIcon className='size-6 text-dark-100' strokeWidth={2.5} />
        </button>
        <h2 className='font-proxima text-24 font-extrabold uppercase leading-47 text-brand-800 md:text-40'>
          YOUR ECHO PERSONALITY
        </h2>
      </div>
      <p className='mb-8 ml-10 font-sans text-16 font-medium leading-27 text-gray-530'>
        From your personality training (Results)
      </p>

      <div className={clsx('wide:mx-auto wide:max-w-[1200px]', contentClass)}>
        <div className='mb-12 flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-12'>
          <div className='flex w-full flex-col gap-10 lg:w-1/2'>
            <ConfigSlider
              label='Communication Style'
              description='How would you describe your typical communication approach?'
              leftLabel='Serious & Professional'
              rightLabel='Playful & Casual'
              value={config.communication_style ?? 50}
              onChange={(val) => updateField('communication_style', val)}
              isModified={isFieldModified('communication_style')}
              onUndo={() => resetField('communication_style')}
              icon={<ChatBubbleLeftRightIcon className='size-6' />}
            />
            <ConfigSlider
              label='Response Length'
              description='How do you typically respond in conversations?'
              leftLabel='Concise & Brief'
              rightLabel='Detailed & Thorough'
              value={config.response_length ?? 50}
              onChange={(val) => updateField('response_length', val)}
              isModified={isFieldModified('response_length')}
              onUndo={() => resetField('response_length')}
              icon={<DocumentTextIcon className='size-6' />}
            />
          </div>
          <div className='flex w-full flex-col gap-10 lg:w-1/2'>
            <ConfigSlider
              label='Motivation Intensity'
              description='When motivating others, you prefer to be:'
              leftLabel='Gentle & Encouraging'
              rightLabel='Direct & Intense'
              value={config.motivation_intensity ?? 50}
              onChange={(val) => updateField('motivation_intensity', val)}
              isModified={isFieldModified('motivation_intensity')}
              onUndo={() => resetField('motivation_intensity')}
              icon={<BoltIcon className='size-6' />}
            />
            <ConfigSlider
              label='Formality Level'
              description='What level of formality feels natural?'
              leftLabel='Very Formal'
              rightLabel='Very Casual'
              value={config.formality_level ?? 50}
              onChange={(val) => updateField('formality_level', val)}
              isModified={isFieldModified('formality_level')}
              onUndo={() => resetField('formality_level')}
              icon={<BuildingOfficeIcon className='size-6' />}
            />
          </div>
        </div>

        <div className='mb-12 flex flex-col gap-10 lg:flex-row lg:gap-12'>
          <div className='flex w-full flex-col gap-10 lg:w-1/2'>
            <ConfigTags
              label='Topics to Emphasize'
              description='Which topics should your Echo emphasize?'
              options={CONFIG_TOPICS}
              selected={config.topics_to_emphasize ?? []}
              onChange={(val) => updateField('topics_to_emphasize', val)}
              isModified={isFieldModified('topics_to_emphasize')}
              onUndo={() => resetField('topics_to_emphasize')}
              icon={<TrophyIcon className='size-6' />}
            />
            <ConfigTags
              label='Interaction Style'
              description='What experience do you want fans to have?'
              options={CONFIG_INTERACTION}
              selected={config.interaction_style ?? []}
              onChange={(val) => updateField('interaction_style', val)}
              isModified={isFieldModified('interaction_style')}
              onUndo={() => resetField('interaction_style')}
              icon={<UserGroupIcon className='size-6' />}
            />
            <ConfigListInput
              label='Core Values'
              description='What core values guide your life?'
              value={config.core_values ?? ''}
              onChange={(val) => updateField('core_values', val)}
              isModified={isFieldModified('core_values')}
              onUndo={() => resetField('core_values')}
              icon={<LightBulbIcon className='size-6' />}
              placeholder='e.g., Hard work, discipline, loyalty'
            />
            <ConfigListInput
              label='Notes'
              description='What special instructions or limits you have'
              value={config.notes ?? ''}
              onChange={(val) => updateField('notes', val)}
              isModified={isFieldModified('notes')}
              onUndo={() => resetField('notes')}
              icon={<PencilSquareIcon className='size-6' />}
              placeholder='e.g., I never talk about politics...'
            />
          </div>
          <div className='flex w-full flex-col gap-10 lg:w-1/2'>
            <ConfigTags
              label='Motivational Approach'
              description='How do you motivate others?'
              options={CONFIG_APPROACH}
              selected={config.motivational_approach ?? []}
              onChange={(val) => updateField('motivational_approach', val)}
              isModified={isFieldModified('motivational_approach')}
              onUndo={() => resetField('motivational_approach')}
              icon={<HandRaisedIcon className='size-6' />}
            />
            <ConfigTags
              label='Hobbies & Interests'
              description='What are your main interests?'
              options={CONFIG_HOBBIES}
              selected={config.hobbies_interests ?? []}
              onChange={(val) => updateField('hobbies_interests', val)}
              isModified={isFieldModified('hobbies_interests')}
              onUndo={() => resetField('hobbies_interests')}
              icon={<HeartIcon className='size-6' />}
              allowCustom
              gridCols={2}
            />
            <BannedContentNotice
              acknowledged={config.banned_content_acknowledged ?? false}
              onToggle={(checked) => updateField('banned_content_acknowledged', checked)}
            />
          </div>
        </div>
      </div>

      <div className='mt-16 flex w-full flex-col items-center gap-6 md:relative md:flex-row md:justify-center'>
        <div className='flex flex-col-reverse items-center gap-6 md:flex-row md:gap-8'>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            variant='purple-dark'
            className='h-[48px] w-[280px] text-16 font-bold uppercase tracking-wide shadow-sm'
          >
            {isSubmitting ? 'SAVING...' : 'SAVE & TEST ECHO'}
          </Button>

          {hasModifications && (
            <button
              onClick={resetAll}
              disabled={isSubmitting}
              className='font-sans text-20 font-bold uppercase leading-27 tracking-normal text-black transition-opacity hover:opacity-70 disabled:opacity-50'
              type='button'
            >
              CANCEL CHANGES
            </button>
          )}
        </div>

        <div className='md:absolute md:right-0'>
          <button
            onClick={handleSaveClick}
            disabled={isSubmitting}
            className='font-sans text-16 font-semibold leading-27 tracking-normal text-gray-650 underline decoration-solid decoration-auto transition-colors hover:text-brand-800 disabled:opacity-50'
            type='button'
          >
            SAVE FOR LATER
          </button>
        </div>
      </div>
    </div>
  );
};
